import { isNotNullOrUndefined, isNullOrUndefined } from '@utils/utils.tsx';
import dayjs, { Dayjs } from 'dayjs';
import { createLogger } from '@utils/logger';
import {
  DayEnum,
  DaysWithIntervalsDTO,
  GlobalDayWithInterval,
  TimeResponse,
  TimeslotCalendarDTO,
  TimeSlotProgressTabDTO,
  TimeslotOutDTO,
  TimeSlotResponse,
} from '@api/ringil-api.v2.tsx';

const log = createLogger('dateWithTz');

export const shortDateFormat = 'd.m.y';

export const longDateFormat = 'd. m. yy';

// dayjs generic

/**
 * This function generate days inside interval.
 * @param start
 * @param end
 */
export const dayJsInterval = (start: Dayjs, end: Dayjs): Dayjs[] => {
  if (isNullOrUndefined(start)) return null;
  if (isNullOrUndefined(end) || !end.isValid()) return [start];

  let date = start;
  const a = [];

  while (date.isSameOrBefore(end, 'day')) {
    a.push(date);
    log.debug(`Adding date: ${date}`);
    date = date.add(1, 'day');
  }

  log.debug(`daysInterval: ${JSON.stringify(a)}`);
  return a;
};

export const dayjsNowInTz = (timezoneId: string): dayjs.Dayjs => {
  if (isNullOrUndefined(timezoneId)) return null;

  const result = dayjs().tz(timezoneId);
  log.debug(`dayjsNowInTz: Timezone: ${timezoneId}, Result: ${result.format()}`);
  return result;
};

export const dayjsAtStartOfDayInTz = (date: dayjs.Dayjs, timezoneId: string): dayjs.Dayjs => {
  if (isNullOrUndefined(date) || isNullOrUndefined(timezoneId)) return null;

  const result = dayjs.tz(date, timezoneId).startOf('day');
  log.debug(`dayjsAtStartOfDayInTz: Input date: ${date.toString()}, Timezone: ${timezoneId}, Result: ${result.format()}`);
  return result;
};

export const dayjsWithSetTime = (previous: Dayjs, time: string) => {
  if (isNullOrUndefined(previous) || isNullOrUndefined(time)) return null;
  const split = time.split(':');
  const newTime = previous.set('hour', parseInt(split[0])).set('minute', parseInt(split[1])).set('second', 0).set('millisecond', 0);
  log.debug(`dayjsWithSetTime: previous: ${previous.format()}, time: ${time}, newTime: ${newTime.format()}`);
  return newTime;
};



// dayJs from...

export const dayjsFromDateAtMidnightInTz = (
  pickerDate: Date | Date[],
  timezoneId: string
): dayjs.Dayjs | dayjs.Dayjs[] => {
  if (isNullOrUndefined(pickerDate) || isNullOrUndefined(timezoneId)) return null;

  const convert = (d: Date): dayjs.Dayjs => {
    if (isNullOrUndefined(d)) return null;

    const year = d.getFullYear();
    const month = d.getMonth() + 1; // JS months are 0-indexed
    const day = d.getDate();

    const dateString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')} 00:00:00`;
    const result = dayjs.tz(dateString, timezoneId).utc();

    log.debug(`dayjsFromDateAtMidnightInTz: Input date: ${d.toString()}, Extracted: ${year}-${month}-${day}, Timezone: ${timezoneId}, Result: ${result.format()}`);
    return result;
  };

  if (Array.isArray(pickerDate)) {
    const converted = pickerDate
      .filter(d => !isNullOrUndefined(d))
      .map(convert);
    return converted.length > 0 ? converted : null;
  }
  return convert(pickerDate);
};


export const dayjsFromDateTimeInTz = (
  pickerDateTime: Date | Date[],
  timezoneId: string
): dayjs.Dayjs | dayjs.Dayjs[] => {
  if (isNullOrUndefined(pickerDateTime) || isNullOrUndefined(timezoneId)) return null;

  const convert = (d: Date): dayjs.Dayjs => {
    if (isNullOrUndefined(d)) return null;

    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const day = d.getDate();
    const hour = d.getHours();
    const minute = d.getMinutes();
    const second = d.getSeconds();

    const dateTimeString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')} ${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:${second.toString().padStart(2, '0')}`;
    const result = dayjs.tz(dateTimeString, timezoneId).utc();

    log.debug(`dayjsFromDateTimeInTz: Input: ${d.toISOString()}, Extracted: ${dateTimeString}, Timezone: ${timezoneId}, Result: ${result.format()}`);
    return result;
  };

  if (Array.isArray(pickerDateTime)) {
    const converted = pickerDateTime
      .filter(d => !isNullOrUndefined(d))
      .map(convert);
    return converted.length > 0 ? converted : null;
  }
  return convert(pickerDateTime);
};

export const dayjsFromLocalDateString = (localDateString: string): dayjs.Dayjs => {
  if (isNullOrUndefined(localDateString)) return null;

  const date = dayjs(localDateString, 'YYYY-MM-DD');
  log.debug(`dayjsFromLocalDateString: Input: ${localDateString}, Result: ${date.format()}`);
  return date;
};

export const dayjsFromApiUtcInTz = (
  apiString: string,
  timezoneId: string
): dayjs.Dayjs => {
  if (isNullOrUndefined(apiString) || isNullOrUndefined(timezoneId)) return null;

  const parsed = dayjs(apiString).utc();
  const result = parsed.tz(timezoneId);
  log.debug(`dayjsFromApiUtcInTz: UTC ISO: ${apiString}, Timezone: ${timezoneId}, Result: ${result.format()}`);
  return result;
};

export const dayjsFromApiUtc = (
  apiString: string,
): dayjs.Dayjs => {
  if (isNullOrUndefined(apiString)) return null;

  const parsed = dayjs.tz(apiString, 'UTC');
  log.debug(`dayjsFromApiUtc: UTC ISO: ${apiString}, Result: ${parsed.format()}`);
  return parsed;
};

export const dayjsFromApiPrague = (
  apiString: string,
): dayjs.Dayjs => {
  if (isNullOrUndefined(apiString)) return null;

  const parsed = dayjs.tz(apiString, 'Europe/Prague');
  const result = parsed.utc();
  log.debug(`dayjsFromApiPrague: UTC ISO: ${apiString}, Result: ${parsed.format()} converted to UTC: ${result.format()}`);
  return result;
};

export const dayjsFromApiUtcDateAtMidnightInTz = (
  dateString: string,
  timezoneId: string
): dayjs.Dayjs => {
  if (isNullOrUndefined(dateString) || isNullOrUndefined(timezoneId)) return null;

  log.debug(`dayjsFromApiUtcDateAtMidnightInTz: Input date string: ${dateString}, Timezone: ${timezoneId}`);
  const result = dayjs.tz(`${dateString}T00:00:00.000Z`, timezoneId);
  log.debug(`dayjsFromApiUtcDateAtMidnightInTz: Date: ${dateString}, Timezone: ${timezoneId}, Result: ${result.format()}`);
  return result;
};

export const dayjsFromDayAndTimeStrings = (day: string, time: string, timezoneId: string): dayjs.Dayjs => {
  if (isNullOrUndefined(day) || isNullOrUndefined(time) || isNullOrUndefined(timezoneId)) return null;
  log.debug(`dayjsFromDayAndTimeStrings: Input day: ${day}, time: ${time}, timezoneId: ${timezoneId}`);
  const dateTimeString = `${day}T${time}`;
  const dayJsInTz = dayjs.tz(dateTimeString, timezoneId);
  const dayJsInUtc = dayJsInTz.utc();
  log.debug(`dayJsFromDayAndTimeStrings: dayJsInTz: ${dayJsInTz.toString()}, dayJsInUtc: ${dayJsInUtc.toString()}`);
  return dayJsInUtc;
};


// Date from...
export const dateFromDayjsInTz = (
  dayjsValue: dayjs.Dayjs | dayjs.Dayjs[],
  timezoneId: string
): Date | Date[] => {
  if (isNullOrUndefined(dayjsValue) || isNullOrUndefined(timezoneId)) return null;

  const convert = (d: dayjs.Dayjs): Date => {
    const inTz = d.tz(timezoneId);
    const year = inTz.year();
    const month = inTz.month(); // 0-indexed for Date constructor
    const day = inTz.date();
    const hour = inTz.hour();
    const minute = inTz.minute();
    const second = inTz.second();

    const result = new Date(year, month, day, hour, minute, second);

    log.debug(`dateFromDayjsInTz: Input dayjs: ${d.format()}, Timezone: ${timezoneId}, Result Date: ${result.toISOString()}`);
    return result;
  };

  if (Array.isArray(dayjsValue)) {
    return dayjsValue.map(convert);
  }
  return convert(dayjsValue);
};


export const dateFromDayjs = (date: Dayjs) => dayjs(date).toDate();

export const dateFromApiStringInTz = (apiString: string, timezoneId: string): Date => {
  if (isNullOrUndefined(apiString)) return null;
  return dateFromDayjsInTz(dayjsFromApiUtcInTz(apiString, timezoneId), timezoneId) as Date;
};


// api Prague from...

export const apiPragueFromDayjsInTz = (
  dayjsValue: dayjs.Dayjs,
  timezoneId: string
): string => {
  if (isNullOrUndefined(dayjsValue)) return null;

  const calendarDate = dayjsValue.format('YYYY-MM-DD');

  const sourceOffset = dayjsValue.utcOffset(); // offset in minutes from UTC
  const pragueAtSameDate = dayjs.tz(calendarDate, 'Europe/Prague');
  const pragueOffset = pragueAtSameDate.utcOffset(); // offset in minutes from UTC
  const offsetDifference = pragueOffset - sourceOffset;
  log.debug(`apiPragueFromDayjsInTz: Timezone: ${timezoneId}, Date: ${calendarDate}, Source offset: ${sourceOffset}, Prague offset: ${pragueOffset}, Difference: ${offsetDifference} minutes`);

  const adjustedTime = dayjsValue.add(offsetDifference, 'minutes');
  const result = adjustedTime.utc().toISOString();

  log.debug(`apiPragueFromDayjsInTz: Input: ${dayjsValue.format()}, Adjusted to Prague: ${adjustedTime.format()}, Result (as UTC): ${result}`);
  return result;
};

/**
 *
 * @param dayjsValue dayjs in utc of the source timezone
 * @param timezoneId source timezone
 * @return value in Prague timezone
 */
export const apiPragueFromDayjsUtc = (dayjsValue: dayjs.Dayjs, timezoneId: string): string => {
  if (isNullOrUndefined(dayjsValue) || isNullOrUndefined(timezoneId)) return null;

  const sourceOffset = dayjsValue.tz(timezoneId).utcOffset(); // offset in minutes from UTC
  const pragueOffset = dayjs().tz('Europe/Prague').utcOffset(); // offset in minutes from UTC
  const offsetDifference = pragueOffset - sourceOffset;

  const adjustedTime = dayjsValue.add(-offsetDifference, 'minutes');
  log.debug(`apiPragueFromDayjsUtc: Input UTC: ${dayjsValue.format()}, Prague offset: ${pragueOffset}, Source offset: ${sourceOffset}, Difference: ${offsetDifference} minutes, Adjusted to Prague: ${adjustedTime.format()}`);
  return adjustedTime.toISOString();
};

export const apiPragueFromDayjsLocal = (dayjsValue: dayjs.Dayjs): string => {
  if (isNullOrUndefined(dayjsValue)) return null;

  const result = dayjsValue.tz('Europe/Prague').format();
  log.debug(`apiPragueFromDayjsLocal: Input: ${dayjsValue.format()}, Result in Prague (as UTC): ${result}`);
  return result;
};

export const apiPragueFromDayjsLocalWithFormat = (localDateTime: dayjs.Dayjs, timezoneId: string): string => {
  if (isNullOrUndefined(localDateTime)) return null;
  return dayjs.tz(localDateTime, timezoneId).format('YYYY-MM-DDTHH:mm:ss');
};


// api utc from...

export const apiUtcFromDayjs = (
  dayjsValue: dayjs.Dayjs
): string => {
  if (isNullOrUndefined(dayjsValue)) return null;

  const result = dayjsValue.toISOString();
  log.debug(`apiUtcFromDayjs: Input: ${dayjsValue.format()}, UTC: ${result}`);
  return result;
};


export const apiUtcDateFromDayjs = (
  dayjsValue: dayjs.Dayjs
): string => {
  if (isNullOrUndefined(dayjsValue)) return null;

  // Get the calendar date as it appears in the source timezone
  // This is what the backend needs for utcDay field
  const result = dayjsValue.format('YYYY-MM-DD');

  log.debug(`apiUtcDateFromDayjs: Input: ${dayjsValue.format()}, UTC date (calendar in source): ${result}`);
  return result;
};


// string from...

export const stringDateTimeWithLocaleFromApiStringInTz = (date: string, timezoneId: string): string => {
  if (isNullOrUndefined(date) || isNullOrUndefined(timezoneId)) return null;
  return dayjsFromApiUtcInTz(date, timezoneId).tz(timezoneId).format('L LT');
};

// Same as above but with seconds (L LTS) — the audit log needs second-level precision to order/distinguish
// entries within the same minute.
export const stringDateTimeSecondsWithLocaleFromApiStringInTz = (date: string, timezoneId: string): string => {
  if (isNullOrUndefined(date) || isNullOrUndefined(timezoneId)) return null;
  return dayjsFromApiUtcInTz(date, timezoneId).tz(timezoneId).format('L LTS');
};

/**
 * This will be in use util we remove legacy TimeResponse
 * @param dateTimeResponse
 * @deprecated use it until we will drop TimeResponse from API
 */
export const stringFromTimeResponse = (dateTimeResponse: TimeResponse): string => {
  if (dateTimeResponse == null) return null;
  return `${dateTimeResponse.localDate}T${dateTimeResponse.hourString}`;
};


export const stringIsoFromDateInTz = (date: Date, timezoneId: string): string => {
  if (isNullOrUndefined(date) || isNullOrUndefined(timezoneId)) return null;

  const dayjsInTz = dayjs.tz(date, timezoneId);
  const result = dayjsInTz.toISOString();

  log.debug(`stringIsoFromDateInTz: Input date: ${date.toString()}, Timezone: ${timezoneId}, Result ISO: ${result}`);
  return result;
};

export const stringYMDFromDate = (date: Date): string => {
  if (isNullOrUndefined(date)) return null;

  // we ignore the time part and just return the year mont and day only
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // JS months are 0-indexed
  const day = date.getDate().toString().padStart(2, '0');

  const result = `${year}-${month}-${day}`;

  log.debug(`stringYMDFromDate: Input date: ${date.toString()}, Result ISO: ${result}`);
  return result;
}

export const stringYMDFromDayjs = (date: dayjs.Dayjs): string => {
  if (isNullOrUndefined(date)) return null;

  const result = date.format('YYYY-MM-DD');

  log.debug(`stringYMDFromDayjs: Input date: ${date.format()}, Result: ${result}`);
  return result;
};

export const stringHHmmFromDayjs = (date: Dayjs): string => {
  if (isNullOrUndefined(date)) return null;
  return date.format('HH:mm');
};

export const stringYMDFromStringInTz = (date:string, timezoneId: string): string => {
  if (isNullOrUndefined(date) || isNullOrUndefined(timezoneId)) return null;
  const dayjsInTz = dayjsFromApiUtcInTz(date, timezoneId);
  const result = dayjsInTz.format('L');
  log.debug(`stringYMDFromStringInTz: Input date: ${date}, Timezone: ${timezoneId}, Result: ${result}`);
  return result;
};

export const stringHHmmFromStringInTz = (date: string, timezoneId: string): string => {
  if (isNullOrUndefined(date) || isNullOrUndefined(timezoneId)) return null;

  const dayjsInTz = dayjsFromApiUtcInTz(date, timezoneId);
  const result = dayjsInTz.format('HH:mm');
  log.debug(`stringHHmmFromStringInTz: Input date: ${date}, Timezone: ${timezoneId}, Result: ${result}`);
  return result;
}

export const stringFromTimeslotResponse = (timeslot: TimeSlotResponse): string => {
  if (isNullOrUndefined(timeslot)) return null;

  if (isNotNullOrUndefined(timeslot.utcStart) && isNotNullOrUndefined(timeslot.utcEnd) && isNotNullOrUndefined(timeslot.timezoneId)) {
    const since = dayjsFromApiUtcInTz(timeslot.utcStart, timeslot.timezoneId);
    const till  = dayjsFromApiUtcInTz(timeslot.utcEnd,   timeslot.timezoneId);
    return `${formatDateFromDayjsWithLocale(since)} | ${stringHHmmFromDayjs(since)} - ${stringHHmmFromDayjs(till)}`;
  }

  // legacy fallback — localDate/sinceHourString/tillHourString are plain strings, no conversion needed
  return `${formatDateFromStringWithLocale(timeslot.localDate)} | ${timeslot.sinceHourString} - ${timeslot.tillHourString}`;
};


// formatter

export const formatDateFromDayjsInTzWithLocale = (
  dayjsValue: dayjs.Dayjs,
  timezoneId: string
): string => {
  if (isNullOrUndefined(dayjsValue) || isNullOrUndefined(timezoneId)) return null;

  const inTz = dayjsValue.tz(timezoneId);
  return inTz.format('L');
};

export const formatDateFromDayjsWithLocale = (
  dayjsValue: dayjs.Dayjs,
): string => {
  if (isNullOrUndefined(dayjsValue)) return null;

  return dayjsValue.format('L');
};

export const formatTimeFromDayjsWithLocale = (
  dayjsValue: dayjs.Dayjs,
): string => {
  if (isNullOrUndefined(dayjsValue)) return null;

  return dayjsValue.format('LT');
};

export const formatTime24hFromDayjs = (
  dayjsValue: dayjs.Dayjs,
): string => {
  if (isNullOrUndefined(dayjsValue)) return null;

  return dayjsValue.format('HH:mm');
};

export const formatDateTimeFromDayjsInTzWithLocale = (
  dayjsValue: dayjs.Dayjs,
  timezoneId: string,
  separator: string = ' '
): string => {
  if (isNullOrUndefined(dayjsValue) || isNullOrUndefined(timezoneId)) return null;

  log.debug(`formatDateTimeFromDayjsInTzWithLocale: Input dayjs: ${dayjsValue.format()}, Timezone: ${timezoneId}`);

  const inTz = dayjsValue.tz(timezoneId);
  return `${inTz.format('L')}${separator}${inTz.format('LT')}`;
};

export const formatTimeFromDayjsInTz = (
  dayjsValue: dayjs.Dayjs,
  timezoneId: string
): string => {
  if (isNullOrUndefined(dayjsValue) || isNullOrUndefined(timezoneId)) return null;

  const inTz = dayjsValue.tz(timezoneId);
  return inTz.format('HH:mm');
};

export const formatTimeIntervalFromDayjsIntervalWithLocale = (
  start: dayjs.Dayjs,
  end: dayjs.Dayjs,
  timezoneId: string,
  separator: string = ' - '
): string => {
  if (isNullOrUndefined(start) || isNullOrUndefined(end) || isNullOrUndefined(timezoneId)) return null;

  const startInTz = start.tz(timezoneId);
  const endInTz = end.tz(timezoneId);

  return `${startInTz.format('LT')}${separator}${endInTz.format('LT')}`;
}

export const formatTimeIntervalFromStringInTzWithLocale = (isWholeDay: boolean, since: string, till: string, timezoneId: string, t: any): string => {
  if (isNullOrUndefined(since) || isNullOrUndefined(till) || isNullOrUndefined(timezoneId)) return null;
  if (isWholeDay) {
    return `${t('TimeListItem.isWholeDay')}`
  }

  const startInTz = dayjsFromApiUtcInTz(since, timezoneId);
  const endInTz = dayjsFromApiUtcInTz(till, timezoneId);
  log.debug(`formatTimeIntervalFromStringInTzWithLocale: since: ${since}, till: ${till}, timezoneId: ${timezoneId}, startInTz: ${startInTz.format()}, endInTz: ${endInTz.format()}`);
  return `${startInTz.format('LT')} - ${endInTz.format('LT')}`;
}
/**
 *
 * @param date
 * @param shortYear
 * @deprecated use formatDateFromDayjsWithLocale and make sure dayJs is UTC
 */
export const formatOnlyDateShortFromDayJs = (date: Dayjs, shortYear = false): string => {
  if (isNullOrUndefined(date)) return null;
  if (shortYear) {
    return date.format('D.M.YY');
  } else {
    return date.format('D.M.YYYY');
  }
};

export const formatOnlyDayMonthFromDayJs = (date: Dayjs): string => {
  if (isNullOrUndefined(date)) return null;
  return date.format('D.M');
};

export const formatOnlyDayMonthFromDayjsInTz = (date: Dayjs, timezoneId: string): string => {
  if (isNullOrUndefined(date) || isNullOrUndefined(timezoneId)) return null;
  return date.tz(timezoneId).format('D.M.');
};

export const formatSimpleDateTimeFromDayJs = (date: Dayjs): string => {
  if (isNullOrUndefined(date)) return null;
  return date.format();
};

export const formatDateTimeInTzWithLocale = (date: string, timezoneId: string): string => {
  if (isNullOrUndefined(date) || isNullOrUndefined(timezoneId)) return null;
  return dayjs(date).tz(timezoneId).format('L LTS');
};

export const formatDateInTzWithLocale = (date: string, timezoneId: string): string => {
  if (isNullOrUndefined(date) || isNullOrUndefined(timezoneId)) return null;
  return dayjs(date).tz(timezoneId).format('L');
};

export const formatDateFromStringWithLocale = (localDateString: string) : string => {
  if (isNullOrUndefined(localDateString)) return null;
  // Handle both YYYY-MM-DD and full ISO datetime strings
  const date = localDateString.includes('T')
    ? dayjs(localDateString)  // Parse as ISO datetime
    : dayjs(localDateString, 'YYYY-MM-DD');  // Parse as date-only format
  return date.format('L');
}

export const formatDateTimeInTzForFullCalendar = (date: string, timezoneId: string): string | undefined => {
  if (isNullOrUndefined(date) || isNullOrUndefined(timezoneId)) return null;
  const dateInTz = dayjs.utc(date).tz(timezoneId);
  const result = dateInTz.format('YYYY-MM-DDTHH:mm');
  log.debug(`formatDateTimeInTzForFullCalendar: Input date: ${date}, Timezone: ${timezoneId}, Date in TZ: ${dateInTz.format()}, Result: ${result}`);
  return result;
};


// other utils

export const isSameDayInTz = (date: string, timezoneId: string): boolean => {
  if (isNullOrUndefined(date) || isNullOrUndefined(timezoneId)) return false;
  return dayjsNowInTz(timezoneId).isSame(dayjsFromApiUtcInTz(date, timezoneId), 'day');
};


export const dayjsWithTime = (previous: Dayjs, time: string) => {
  if (isNullOrUndefined(previous) || isNullOrUndefined(time)) return null;
  const splited = time.split(':');
  const newTime = previous.set('hour', parseInt(splited[0])).set('minute', parseInt(splited[1])).set('second', 0).set('millisecond', 0);
  log.debug(`dayjsWithTime: previous: ${previous.format()}, time: ${time}, newTime: ${newTime.format()}`);
  return newTime;
};

/**
 * Returns the next occurrence of dayInWeek after date.
 * NOTE: .day() operates in the timezone of the Dayjs object itself.
 * Pass a Dayjs already converted to the intended timezone — UTC Dayjs may give a different weekday near midnight.
 */
export const findNextDay = (date: Dayjs, dayInWeek: DayEnum): Dayjs => {
  if (isNullOrUndefined(date) || isNullOrUndefined(dayInWeek)) return null;
  let nextDay = date.add(1, 'day');
  const day = numberFromDayEnum(dayInWeek);

  while (nextDay.day() !== day) {
    nextDay = nextDay.add(1, 'day');
  }
  return nextDay;
};

export const dayEnumFromNumber = (day: number) => {
  if (isNullOrUndefined(day)) return null;
  switch (day) {
    case 0:
      return DayEnum.SUNDAY;
    case 1:
      return DayEnum.MONDAY;
    case 2:
      return DayEnum.TUESDAY;
    case 3:
      return DayEnum.WEDNESDAY;
    case 4:
      return DayEnum.THURSDAY;
    case 5:
      return DayEnum.FRIDAY;
    case 6:
      return DayEnum.SATURDAY;
  }
};

export const numberFromDayEnum = (day: DayEnum): number => {
  if (isNullOrUndefined(day)) return null;
  switch (day) {
    case DayEnum.MONDAY:
      return 1;
    case DayEnum.TUESDAY:
      return 2;
    case DayEnum.WEDNESDAY:
      return 3;
    case DayEnum.THURSDAY:
      return 4;
    case DayEnum.FRIDAY:
      return 5;
    case DayEnum.SATURDAY:
      return 6;
    case DayEnum.SUNDAY:
      return 0;
  }
};


export const timeSlotProgressTabDtoComparator = (a: TimeSlotProgressTabDTO, b: TimeSlotProgressTabDTO, timezoneId: string): number => {
  if (isNullOrUndefined(a) || isNullOrUndefined(b)) return null;
  const aSlotDayjs = timezoneId != null
    ? dayjsFromApiUtcInTz(a.utcStart, timezoneId)
    : dayjsFromApiUtcInTz(a.sinceInUTC, 'Europe/Prague');
  const bSlotDayjs = timezoneId != null
    ? dayjsFromApiUtcInTz(b.utcStart, timezoneId)
    : dayjsFromApiUtcInTz(b.sinceInUTC, 'Europe/Prague');
  return aSlotDayjs.valueOf() - bSlotDayjs.valueOf();
};

export const globalDayWithIntervalComparator = (a: GlobalDayWithInterval, b: GlobalDayWithInterval, timezoneId: string): number => {
  if (isNullOrUndefined(a) || isNullOrUndefined(b)) return null;
  const aDayjs = timezoneId != null ? dayjsFromApiUtcInTz(a.utcDay, timezoneId) : dayjsFromApiUtcInTz(a.day, 'Europe/Prague');
  const bDayjs = timezoneId != null ? dayjsFromApiUtcInTz(b.utcDay, timezoneId) : dayjsFromApiUtcInTz(b.day, 'Europe/Prague');
  return aDayjs.valueOf() - bDayjs.valueOf();
}

/**
 * Updates the time component of a UTC dayjs object through location timezone conversion
 * @param utcDayjs - The dayjs object in UTC mode
 * @param time - Time string in format "HH:mm"
 * @param timezoneId - The timezone to apply the time in
 * @returns Updated dayjs object in UTC mode with new time
 *
 * @example
 * const utcDate = dayjs.utc('2026-02-20T14:00:00Z');
 * const updated = applyTimeToUtcDayjsInTz(utcDate, '15:30', 'America/New_York');
 * // Returns UTC dayjs with time set to 15:30 in New York timezone
 */
export const applyTimeToUtcDayjsInTz = (
  utcDayjs: Dayjs,
  time: string,
  timezoneId: string
): Dayjs => {
  if (isNullOrUndefined(utcDayjs) || isNullOrUndefined(time) || isNullOrUndefined(timezoneId)) {
    console.error('applyTimeToUtcDayjsInTz: Missing required parameters', { utcDayjs, time, timezoneId });
    return null;
  }

  const dateInTz = utcDayjs.tz(timezoneId);
  const updatedInTz = dayjsWithSetTime(dateInTz, time);
  const updatedUtc = updatedInTz.utc();

  log.debug(`applyTimeToUtcDayjsInTz: utc: ${utcDayjs.format()}, time: ${time}, tz: ${timezoneId}, result: ${updatedUtc.format()}`);
  return updatedUtc;
};

/**
 * Changes the date of a UTC dayjs while preserving time in location timezone
 * @param currentUtc - Current UTC dayjs object
 * @param newDate - New date (already in UTC, at midnight in location TZ from Calendar)
 * @param timezoneId - Timezone to extract/preserve time in
 * @returns New dayjs in UTC with date changed but time preserved in timezone
 *
 * @example
 * const current = dayjs.utc('2026-02-20T14:00:00Z'); // 9:00 AM in NY
 * const newDate = dayjs.utc('2026-02-25T05:00:00Z'); // midnight in NY
 * const result = changeDatePreserveTimeInTz(current, newDate, 'America/New_York');
 * // Returns 2026-02-25 at 9:00 AM NY time (14:00 UTC)
 */
export const changeDatePreserveTimeInTz = (
  currentUtc: Dayjs,
  newDate: Dayjs,
  timezoneId: string
): Dayjs => {
  if (isNullOrUndefined(currentUtc) || isNullOrUndefined(newDate) || isNullOrUndefined(timezoneId)) {
    console.error('changeDatePreserveTimeInTz: Missing required parameters', { currentUtc, newDate, timezoneId });
    return null;
  }

  // Extract time from current UTC in location timezone
  const currentInTz = currentUtc.tz(timezoneId);
  const timeHour = currentInTz.hour();
  const timeMinute = currentInTz.minute();

  // Convert newDate to location timezone, then set the time in that timezone
  const newDateInTz = newDate.tz(timezoneId);
  const resultInTz = newDateInTz
    .set('hour', timeHour)
    .set('minute', timeMinute)
    .set('second', 0)
    .set('millisecond', 0);

  // Convert back to UTC
  const result = resultInTz.utc();

  log.debug(`changeDatePreserveTimeInTz: current: ${currentUtc.format()}, currentTime: ${timeHour}:${timeMinute}, newDate: ${newDate.format()}, tz: ${timezoneId}, result: ${result.format()}`);
  return result;
};


/**
 * @param minuteUnitLabel short unit appended to the duration; callers inside components pass the
 *   translated label (`tr('Unit.minutesShort', 'min')`) so the card is not hardcoded to Czech/English.
 */
export const getCalendarEventTimeData = (timeslot: TimeslotCalendarDTO, minuteUnitLabel = 'min') => {
  if (!timeslot) {
    return null;
  }

  const start = isNotNullOrUndefined(timeslot.timezoneId) && isNotNullOrUndefined(timeslot.utcStart) ?
    stringHHmmFromStringInTz(timeslot.utcStart, timeslot.timezoneId) : stringHHmmFromStringInTz(timeslot.since, 'Europe/Prague');
  const end = isNotNullOrUndefined(timeslot.timezoneId) && isNotNullOrUndefined(timeslot.utcEnd) ?
    stringHHmmFromStringInTz(timeslot.utcEnd, timeslot.timezoneId) : stringHHmmFromStringInTz(timeslot.till, 'Europe/Prague');
  const startDate = isNotNullOrUndefined(timeslot.timezoneId) && isNotNullOrUndefined(timeslot.utcStart) ?
    stringYMDFromStringInTz(timeslot.utcStart, timeslot.timezoneId) : stringYMDFromStringInTz(timeslot.since, 'Europe/Prague');
  const endDate = isNotNullOrUndefined(timeslot.timezoneId) && isNotNullOrUndefined(timeslot.utcEnd) ?
    stringYMDFromStringInTz(timeslot.utcEnd, timeslot.timezoneId) : stringYMDFromStringInTz(timeslot.till, 'Europe/Prague');

  const startDayJs = isNotNullOrUndefined(timeslot.timezoneId) && isNotNullOrUndefined(timeslot.utcStart) ?
    dayjsFromApiUtcInTz(timeslot.utcStart, timeslot.timezoneId) : dayjsFromApiUtcInTz(timeslot.since, 'Europe/Prague');

  const endDayJs = isNotNullOrUndefined(timeslot.timezoneId) && isNotNullOrUndefined(timeslot.utcEnd) ?
    dayjsFromApiUtcInTz(timeslot.utcEnd, timeslot.timezoneId) : dayjsFromApiUtcInTz(timeslot.till, 'Europe/Prague');
  const durationMinutes = endDayJs.diff(startDayJs, 'minute');

  log.debug(`getCalendarEventTimeData: timeslot: ${JSON.stringify(timeslot)}, start: ${start}, end: ${end}, startDate: ${startDate}, endDate: ${endDate}, duration: ${durationMinutes} min`);

  return {
    start,
    end,
    startDate,
    endDate,
    durationMinutes,
    duration: `${durationMinutes} ${minuteUnitLabel}`,
    range: `${start} - ${end}`,
    dateRange: startDate === endDate ? startDate : `${startDate} - ${endDate}`,
  };
};


// ---------------------------------------------------------------------------
// TimeslotOutDTO helpers — mirrors backend getFirstTime / getLastTime logic
// ---------------------------------------------------------------------------

/**
 * Returns a Dayjs (UTC) for the start of the loading/unloading window,
 * mirroring the backend `getFirstTime(deliverySlot)`:
 *   - Takes the FIRST day that has at least one interval (non-empty intervals = non-closed).
 *   - If that day has NO intervals (OPEN / whole-day), uses 00:00 of that day.
 *   - Otherwise uses the `since` of the LAST interval on that day.
 *   - Interprets the time in timeslot.timezoneId if present, else 'Europe/Prague'.
 *   - Returns UTC.
 *
 * Returns null if the timeslot is empty or null.
 */
export const getFirstTimeFromTimeslot = (timeslot: TimeslotOutDTO): Dayjs | null => {
  if (isNullOrUndefined(timeslot) || isNullOrUndefined(timeslot.daysWithIntervals)) return null;

  // "non-closed" days: DaysWithIntervalsDTO has no openingMode field.
  // A day with an empty intervals array is treated as CLOSED (no time window).
  const openDays = timeslot.daysWithIntervals.filter(d => isNotNullOrUndefined(d.intervals) && d.intervals.length > 0);
  if (openDays.length === 0) return null;

  const tz = isNotNullOrUndefined(timeslot.timezoneId) ? timeslot.timezoneId : 'Europe/Prague';
  const firstDay: DaysWithIntervalsDTO = openDays[0];

  // Backend uses the LAST interval's sinceInZone from the first open day.
  const time = firstDay.intervals[firstDay.intervals.length - 1].since;

  // Extract just the date part (YYYY-MM-DD) from firstDay.day which may be a full ISO datetime string
  const dateStr = firstDay.day.includes('T') 
    ? firstDay.day.substring(0, 10)  // Extract YYYY-MM-DD from ISO string
    : firstDay.day;  // Already a date string

  const result = dayjs.tz(`${dateStr} ${time}`, tz).utc();
  log.debug(`getFirstTimeFromTimeslot: day=${firstDay.day}, dateStr=${dateStr}, time=${time}, tz=${tz}, utc=${result.format()}`);
  return result;
};

/**
 * Returns a Dayjs (UTC) for the end of the loading/unloading window,
 * mirroring the backend `getLastTime(deliverySlot)`:
 *   - Takes the LAST day that has at least one interval.
 *   - Uses the `till` of the LAST interval on that day.
 *   - Handles the special backend "24:00" string by rolling over to 00:00 of the next day.
 *   - Interprets the time in timeslot.timezoneId if present, else 'Europe/Prague'.
 *   - Returns UTC.
 *
 * Returns null if the timeslot is empty or null.
 */
export const getLastTimeFromTimeslot = (timeslot: TimeslotOutDTO): Dayjs | null => {
  if (isNullOrUndefined(timeslot) || isNullOrUndefined(timeslot.daysWithIntervals)) return null;

  const openDays = timeslot.daysWithIntervals.filter(d => isNotNullOrUndefined(d.intervals) && d.intervals.length > 0);
  if (openDays.length === 0) return null;

  const tz = isNotNullOrUndefined(timeslot.timezoneId) ? timeslot.timezoneId : 'Europe/Prague';
  const lastDay: DaysWithIntervalsDTO = openDays[openDays.length - 1];

  // Backend uses the LAST interval's tillInZone from the last open day.
  const till = lastDay.intervals[lastDay.intervals.length - 1].till;

  // Extract just the date part (YYYY-MM-DD) from lastDay.day which may be a full ISO datetime string
  const dateStr = lastDay.day.includes('T')
    ? lastDay.day.substring(0, 10)  // Extract YYYY-MM-DD from ISO string
    : lastDay.day;  // Already a date string

  // Mirror backend createZonedDateTimeTillWith24HoursFormat:
  // "24:00" means midnight at the start of the NEXT day.
  let result: Dayjs;
  if (till === '24:00') {
    result = dayjs.tz(`${dateStr} 00:00`, tz).add(1, 'day').utc();
  } else {
    result = dayjs.tz(`${dateStr} ${till}`, tz).utc();
  }

  log.debug(`getLastTimeFromTimeslot: day=${lastDay.day}, dateStr=${dateStr}, till=${till}, tz=${tz}, utc=${result.format()}`);
  return result;
};

/**
 * Returns the loading start date as a display string with locale in the
 * timeslot's own timezone, derived from the first open slot window.
 * Encapsulates getFirstTimeFromTimeslot + timezone formatting so call sites
 * don't need to repeat the UTC→localTz dance.
 *
 * Returns null if the timeslot is null/empty.
 */
export const formatLoadingDateFromTimeslot = (timeslot: TimeslotOutDTO): string | null => {
  const firstTime = getFirstTimeFromTimeslot(timeslot);
  if (firstTime == null) return null;
  const tz = isNotNullOrUndefined(timeslot?.timezoneId) ? timeslot.timezoneId : 'Europe/Prague';
  return firstTime.tz(tz).format('L');
};

// ─── Time-string utilities (not timezone-related) ────────────────────────────

/**
 * Converts an "HH:mm" string to a JS Date with that time set on today.
 * Used exclusively with PrimeReact <Calendar timeOnly> which reads only the
 * hours/minutes from the Date object — the date part and timezone are irrelevant.
 * Do NOT use this to build a full datetime — for that, use dayjsFromDayAndTimeStrings(day, time, tz).
 */
export function timeStringToDate(time?: string): Date | null {
  if (isNullOrUndefined(time)) return null;
  const [hour, minute] = time.split(':').map(Number);
  const date = new Date();
  date.setHours(hour || 0, minute || 0, 0, 0);
  return date;
}

/**
 * Converts a JS Date to an "HH:mm" string.
 * Used exclusively with PrimeReact <Calendar timeOnly> onChange — reads hours/minutes
 * from the Date the picker emits. Timezone is irrelevant; do not use for full datetime conversion.
 */
export function dateToTimeString(date: Date): string | null {
  if (isNullOrUndefined(date)) return null;
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

/**
 * Converts a duration in minutes to an "HH:mm" string.
 * Pure math — not timezone-related.
 */
export function minutesToTimeString(totalMinutes: number): string {
  if (isNullOrUndefined(totalMinutes)) return null;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

// ─── Browser timezone detection ────────────────────────────────────────────────

/**
 * Returns the IANA timezone ID of the user's browser (e.g. 'Europe/Prague', 'America/New_York').
 * This is the local timezone where the browser is running, NOT a user preference or default timezone.
 * Use this to populate filter DTOs and other contexts that need the browser's actual timezone.
 */
export const getBrowserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;
